// @ts-nocheck
import axios from "axios";
import { getApiBase } from "@/lib/velxio/lib/apiBase";
import type {
  ESP32BoardOptions,
  SpiffsFile,
} from "@/lib/velxio/types/boardOptions";

export interface SketchFile {
  name: string;
  content: string;
}

/**
 * Per-board build options forwarded to the backend. Only meaningful for
 * ESP32 targets — `board_options` is structurally translated into sdkconfig
 * knobs and a generated partitions.csv by the ESP-IDF compiler. `spiffs_files`
 * (if non-empty) are baked into a SPIFFS partition image via `mkspiffs`.
 */
export interface CompileExtras {
  boardOptions?: ESP32BoardOptions;
  spiffsFiles?: SpiffsFile[];
}

export interface CompileResult {
  success: boolean;
  hex_content?: string;
  binary_content?: string; // base64-encoded .bin for RP2040
  binary_type?: "bin" | "uf2";
  has_wifi?: boolean; // True when sketch uses WiFi (ESP32 only)
  stdout: string;
  stderr: string;
  error?: string;
  core_install_log?: string;
}

interface CompileStartResponse {
  job_id: string;
}

interface CompileStatusResponse {
  state: "pending" | "running" | "done" | "error";
  started_at: number;
  finished_at: number | null;
  stdout: string;
  result: CompileResult | null;
  error: string | null;
}

/**
 * Live progress callback — called on every poll while state ∈ {pending,
 * running}. `stdout` is the full live cmake + ninja output captured so far
 * (cap of ~256 KB on the server side, tail kept). Caller can compute a
 * delta against the previous call if it wants to append-only render.
 */
export type CompileProgress = (info: {
  state: "pending" | "running";
  stdout: string;
  elapsedSeconds: number;
}) => void;

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 15 * 60 * 1000; // 15 minutes — covers cold ESP-IDF builds

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Compile a sketch via the async job pipeline.
 *
 *   POST /compile/start                  → { job_id }
 *   GET  /compile/status/<job_id>  (×N)  → { state, stdout, result?, error? }
 *
 * Each individual request returns in milliseconds, so Cloudflare's 100s edge
 * timeout never kicks in — even when the underlying ESP-IDF cold build runs
 * for 5-7 minutes. Falls back to throwing an Error after MAX_POLL_DURATION_MS.
 *
 * `onProgress` (optional): called every poll with the live cmake + ninja
 * output so the editor can stream the compilation console instead of
 * waiting for everything at the end.
 */
export async function compileCode(
  files: SketchFile[],
  board: string = "arduino:avr:uno",
  projectId?: string | null,
  onProgress?: CompileProgress,
  extras?: CompileExtras,
): Promise<CompileResult> {
  console.log(
    "Sending compilation request to:",
    `${getApiBase()}/compile/start`,
  );
  console.log("Board:", board);
  console.log(
    "Files:",
    files.map((f) => f.name),
  );

  // Translate camelCase frontend keys to snake_case backend keys. Backend
  // only inspects these fields for esp32:* FQBNs — other boards pass them
  // through unread.
  const board_options = extras?.boardOptions
    ? { ...extras.boardOptions }
    : null;
  const spiffs_files = extras?.spiffsFiles?.length
    ? extras.spiffsFiles.map((f) => ({
        name: f.name,
        content_b64: f.contentB64,
      }))
    : null;

  let jobId: string;
  try {
    const startResp = await axios.post<CompileStartResponse>(
      `${getApiBase()}/compile/start`,
      {
        files,
        board_fqbn: board,
        project_id: projectId ?? null,
        board_options,
        spiffs_files,
      },
      { withCredentials: true, timeout: 30000 },
    );
    jobId = startResp.data.job_id;
    console.log("[compile] queued job", jobId);
  } catch (error) {
    console.error("Compilation request failed:", error);
    if (axios.isAxiosError(error) && error.response) {
      // Server may return either a structured compile result / FastAPI JSON
      // error or a plain-text 500 body. Always normalize to CompileResult so
      // callers don't try to parse "Internal Server Error" as compiler output.
      const data = error.response.data;
      const detail =
        typeof data === "string"
          ? data
          : typeof data?.detail === "string"
            ? data.detail
            : error.message;
      return {
        success: false,
        stdout: "",
        stderr: detail,
        error: detail,
      };
    }
    throw error instanceof Error
      ? error
      : new Error("No response from server. Is the backend running?");
  }

  const startedAt = Date.now();
  // Initial small delay so we don't hit /status before the background task
  // has even moved past 'pending'.
  await sleep(500);

  while (true) {
    if (Date.now() - startedAt > MAX_POLL_DURATION_MS) {
      throw new Error(
        `Compile timed out client-side after ${Math.round(MAX_POLL_DURATION_MS / 1000)}s`,
      );
    }

    let status: CompileStatusResponse;
    try {
      const resp = await axios.get<CompileStatusResponse>(
        `${getApiBase()}/compile/status/${jobId}`,
        { withCredentials: true, timeout: 30000 },
      );
      status = resp.data;
    } catch (error) {
      // Transient poll error — log, wait, retry. Only abort on 404 (job
      // expired or never existed).
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw new Error(
          `Compile job ${jobId} not found (server may have restarted)`,
        );
      }
      console.warn("[compile] status poll error, retrying:", error);
      await sleep(POLL_INTERVAL_MS);
      continue;
    }

    if (status.state === "done" && status.result) {
      const elapsed = Math.round((Date.now() - startedAt) / 1000);
      console.log(`[compile] job ${jobId} done in ${elapsed}s`);
      return status.result;
    }

    if (status.state === "error") {
      console.error(`[compile] job ${jobId} errored:`, status.error);
      return {
        success: false,
        stdout: status.stdout || "",
        stderr: "",
        error: status.error || "Compile failed",
      };
    }

    // state ∈ {pending, running} — surface live build output if requested
    if (onProgress) {
      try {
        onProgress({
          state: status.state,
          stdout: status.stdout || "",
          elapsedSeconds: Math.round((Date.now() - startedAt) / 1000),
        });
      } catch (err) {
        // A faulty UI hook must never break the polling loop.
        console.warn("[compile] onProgress threw:", err);
      }
    }

    await sleep(POLL_INTERVAL_MS);
  }
}
